/**
 * The admin reads the active club from localStorage on every request
 * (getClubId in src/api/CalendarBooking.tsx). localStorage is shared by all
 * tabs, so a SuperAdmin who switches club in tab B silently moves tab A to the
 * new club while tab A keeps its old location: Services showed other clubs'
 * data (HOP Belfast, 2026-10-27).
 *
 * This file keeps a tiny copy of the getClubId rule so it does not pull the
 * api module (axios, react-query hooks) into every caller.
 */
import { hashKey, onlineManager, type QueryKey } from "@tanstack/react-query";

let cachedRaw: string | null = null;
let cachedClub = "";

function parseOrRaw(raw: string | null): unknown {
  if (raw === null) return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

/**
 * Same rule as getClubId(): SuperAdmin → localStorage "Club_id", everyone
 * else → user.club_id. Returned as a string ("" when unknown) so it compares
 * and hashes the same whether stored as 73 or "73". Never throws.
 */
export function readActiveClubId(): string {
  let userRaw: string | null = null;
  let clubRaw: string | null = null;
  try {
    userRaw = localStorage.getItem("user");
    clubRaw = localStorage.getItem("Club_id");
  } catch {
    return "";
  }
  // Called on every query hash, so skip JSON.parse when nothing changed.
  const raw = `${userRaw}\u0000${clubRaw}`;
  if (raw === cachedRaw) return cachedClub;

  const user = parseOrRaw(userRaw) as any;
  const club =
    user?.role?.name === "SUPER_ADMIN" ? parseOrRaw(clubRaw) : user?.club_id;
  cachedRaw = raw;
  cachedClub =
    typeof club === "number" || typeof club === "string" ? String(club) : "";
  return cachedClub;
}

/**
 * React Query hash with the active club in front, so data cached for one club
 * is never served for another. Key matching (invalidateQueries, setQueryData,
 * partial keys) still works on the plain queryKey.
 */
export function clubScopedQueryKeyHash(queryKey: QueryKey): string {
  return `club:${readActiveClubId()}|${hashKey(queryKey)}`;
}

/**
 * Call when this tab switches club, before the reload. The old page stays
 * alive until the reload lands; pausing React Query stops it from fetching
 * the new club with the old location in that gap.
 */
export function pauseFetchingForClubSwitch(): void {
  onlineManager.setOnline(false);
}

const CLUB_KEYS = new Set<string | null>(["Club_id", "user", null]);

/**
 * Run onChange (default: reload) when another tab changes this tab's club.
 * `storage` events only reach other tabs, never the tab that wrote. A null
 * key means localStorage.clear() (logout). Returns a cleanup function.
 */
export function watchClubChangeFromOtherTabs(
  onChange: () => void = () => window.location.reload()
): () => void {
  let club = readActiveClubId();
  const onStorage = (event: StorageEvent) => {
    if (!CLUB_KEYS.has(event.key)) return;
    const next = readActiveClubId();
    if (next === club) return;
    club = next;
    onChange();
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}
